/**
 * Searchable activity table with priority/energy filters and sortable title, priority, energy,
 * duration, and deadline columns. Derives visible rows from the task store and delegates edits to
 * TaskEditor before API/store reconciliation.
 *
 * Rows stay mounted while a search or filter hides them ({@link TableRow} toggles `hidden`), and
 * each row's cells are memoized on the activity record, so a keystroke flips attributes instead of
 * unmounting and recreating up to every row. After a search, filter or sort change the first
 * {@link FIRST_ROWS} matching rows show at once and the rest follow in the render after that
 * frame's paint, so the input paints without laying out the whole table.
 *
 * @module apps/web/src/components/table/TableView
 */

import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useAfterPaint } from '@/hooks/useAfterPaint';
import { useRouteTasks } from '@/stores/routeData';
import { useTasksStore } from '@/stores/tasksStore';
import { TaskUI } from '@fluxability/types';
import { ActivityDone, activityIsDone } from '@/components/activities/ActivityDone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { Detached, useDetachedState } from '@/hooks/useDetachedState';
import { api } from '@/lib/api';
import { taskToUI, numberToPriority } from '@fluxability/types';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getEnergyColor, getPriorityColor, formatDuration } from '@/lib/utils';
import { Search, SortAsc, SortDesc } from 'lucide-react';

/**
 * Matching rows shown in the same frame as a search, filter or sort change; the remaining rows
 * appear in the render after that frame's paint. Rows are about 70 px tall, so 24 rows fill a
 * 1,700 px viewport below the table header; on a taller screen the last rows arrive a frame later.
 */
const FIRST_ROWS = 24;

/** Activity columns supported by the table’s local comparator. */
type SortField = 'title' | 'priority' | 'energy' | 'duration' | 'deadline';
/** Direction applied to the currently selected table comparator. */
type SortOrder = 'asc' | 'desc';

/**
 * Searchable activity table with priority/energy filters and sortable title, priority, energy,
 * duration, and deadline columns. Derives visible rows from the task store and delegates edits to
 * TaskEditor before API/store reconciliation.
 */
export function TableView() {
  const tasks = useRouteTasks();
  const updateTask = useTasksStore(s => s.updateTask);
  const deleteTask = useTasksStore(s => s.deleteTask);
  // Latest records for the stable row callback below.
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterEnergy, setFilterEnergy] = useState<string>('all');
  // The editor owns the open activity, so opening it re-renders the editor, not every row.
  const editor = useDetachedState<TaskUI | null>();
  const setEditingTask = editor.set;

  /** Open the editor for an activity ID, reading the latest record. */
  const editTask = useCallback(
    (taskId: string) => {
      setEditingTask(tasksRef.current.find(task => task.id === taskId) ?? null);
    },
    [setEditingTask]
  );

  const handleSaveTask = async (updates: Partial<TaskUI>, editingTask: TaskUI | null) => {
    if (!editingTask) return;

    try {
      const input = {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        split_policy: updates.splitPolicy,
        custom_attributes: updates.customAttributes,
      };
      const response = await api.tasks.update(editingTask.id, input);
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI(response.data?.task || response.data));
      } else {
        updateTask(editingTask.id, updates);
      }
    } catch (err) {
      console.error('Failed to save activity', err);
      updateTask(editingTask.id, updates);
    }
  };

  const handleDeleteTask = async (editingTask: TaskUI | null) => {
    if (!editingTask) return;

    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (err) {
      console.error('Failed to delete activity', err);
    }
  };

  // Sort every activity; searching and filtering then only hide rows, so typing never re-sorts.
  const sortedTasks = useMemo(() => {
    const sorted = [...tasks];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'priority':
          comparison = (a.priority || 5) - (b.priority || 5);
          break;
        case 'energy':
          comparison = (a.energyLevel || 0) - (b.energyLevel || 0);
          break;
        case 'duration':
          comparison = (a.estimatedDuration || 0) - (b.estimatedDuration || 0);
          break;
        case 'deadline': {
          const aTime = a.deadline ? new Date(a.deadline).getTime() : Infinity;
          const bTime = b.deadline ? new Date(b.deadline).getTime() : Infinity;
          comparison = aTime - bTime;
          break;
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [tasks, sortField, sortOrder]);

  // IDs of the activities that match the search and filters.
  const shownIds = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const shown = new Set<string>();
    for (const task of tasks) {
      // Search filter
      const matchesSearch =
        (task.title || '').toLowerCase().includes(query) ||
        (task.description || '').toLowerCase().includes(query);

      // Priority filter
      const matchesPriority =
        filterPriority === 'all' || task.priority === parseInt(filterPriority);

      // Energy filter
      const matchesEnergy =
        filterEnergy === 'all' || (task.energyLevel && task.energyLevel === parseInt(filterEnergy));

      if (matchesSearch && matchesPriority && matchesEnergy) shown.add(task.id);
    }
    return shown;
  }, [tasks, searchQuery, filterPriority, filterEnergy]);

  // In the first render after a change, `settledKey` still holds the previous view; rows past
  // FIRST_ROWS stay hidden until the render after the paint, where the two keys agree again.
  const viewKey = [searchQuery, filterPriority, filterEnergy, sortField, sortOrder].join('\u0000');
  const settledKey = useAfterPaint(viewKey);
  const settling = settledKey !== viewKey;
  let shownRank = 0;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = sortOrder === 'asc' ? SortAsc : SortDesc;

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-4 border-b p-4">
        <h2 className="text-xl font-semibold">Activity Table</h2>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search activities..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="1">Priority 1</SelectItem>
              <SelectItem value="2">Priority 2</SelectItem>
              <SelectItem value="3">Priority 3</SelectItem>
              <SelectItem value="4">Priority 4</SelectItem>
              <SelectItem value="5">Priority 5</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterEnergy} onValueChange={setFilterEnergy}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Energy" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Energy</SelectItem>
              <SelectItem value="1">Energy 1</SelectItem>
              <SelectItem value="2">Energy 2</SelectItem>
              <SelectItem value="3">Energy 3</SelectItem>
              <SelectItem value="4">Energy 4</SelectItem>
              <SelectItem value="5">Energy 5</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-background sticky top-0 border-b">
            <tr className="text-left">
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('title')}
                  className="font-semibold"
                >
                  Title
                  {sortField === 'title' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('priority')}
                  className="font-semibold"
                >
                  Priority
                  {sortField === 'priority' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('energy')}
                  className="font-semibold"
                >
                  Energy
                  {sortField === 'energy' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('duration')}
                  className="font-semibold"
                >
                  Duration
                  {sortField === 'duration' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSort('deadline')}
                  className="font-semibold"
                >
                  Deadline
                  {sortField === 'deadline' && <SortIcon className="ml-2 h-4 w-4" />}
                </Button>
              </th>
              <th className="p-4 font-semibold">Attributes</th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map(task => {
              const matches = shownIds.has(task.id);
              const rank = matches ? shownRank++ : -1;
              return (
                <TableRow
                  key={task.id}
                  task={task}
                  hidden={!matches || (settling && rank >= FIRST_ROWS)}
                  onEdit={editTask}
                />
              );
            })}
          </tbody>
        </table>

        {shownIds.size === 0 && (
          <div className="text-muted-foreground py-12 text-center">No activities found</div>
        )}
      </div>

      <Detached state={editor} initial={null}>
        {editingTask => (
          <TaskEditor
            task={editingTask}
            open={!!editingTask}
            onClose={() => setEditingTask(null)}
            onSave={updates => handleSaveTask(updates, editingTask)}
            onDelete={() => handleDeleteTask(editingTask)}
          />
        )}
      </Detached>
    </div>
  );
}

/** Inputs for {@link TableRow}; `onEdit` must be stable for the memo to hold. */
interface TableRowProps {
  /** Activity shown in the row. */
  task: TaskUI;
  /** Hide the row because it does not match the search or filters; it stays mounted. */
  hidden: boolean;
  /** Open the table's editor for an activity ID. */
  onEdit: (taskId: string) => void;
}

/** One activity row; showing or hiding it re-renders only the `<tr>`, not its cells.
 * @component TableRowView
 */
function TableRowView({ task, hidden, onEdit }: TableRowProps) {
  return (
    <tr
      hidden={hidden}
      className="hover:bg-accent/50 cursor-pointer border-b transition-colors"
      onClick={() => onEdit(task.id)}
    >
      <TableRowCells task={task} />
    </tr>
  );
}

/** Memoized {@link TableRowView}. */
const TableRow = memo(TableRowView);

/** The cells of one activity row, memoized on the activity record.
 * @component TableRowCellsView
 */
function TableRowCellsView({
  task,
}: {
  /** Activity whose fields fill the cells. */
  task: TaskUI;
}) {
  return (
    <>
      <td className="p-4">
        <div>
          <div className="flex items-center gap-2">
            <span className={activityIsDone(task) ? 'font-medium text-muted-foreground line-through' : 'font-medium'}>{task.title}</span>
            {activityIsDone(task) && <ActivityDone />}
          </div>
          {task.description && (
            <div className="text-muted-foreground line-clamp-1 text-sm">{task.description}</div>
          )}
        </div>
      </td>
      <td className="p-4">
        <Badge
          variant="outline"
          style={{
            borderColor: getPriorityColor(task.priority),
            color: getPriorityColor(task.priority),
          }}
        >
          P{task.priority}
        </Badge>
      </td>
      <td className="p-4">
        {task.energyLevel && (
          <Badge
            variant="outline"
            style={{
              borderColor: getEnergyColor(task.energyLevel),
              color: getEnergyColor(task.energyLevel),
            }}
          >
            E{task.energyLevel}
          </Badge>
        )}
      </td>
      <td className="p-4 text-sm">
        {task.estimatedDuration ? formatDuration(task.estimatedDuration) : '-'}
      </td>
      <td className="p-4 text-sm">
        {task.deadline ? new Date(task.deadline).toLocaleDateString() : '-'}
      </td>
      <td className="p-4">
        <div className="flex flex-wrap gap-1">
          {task.attributes?.map(attr => (
            <Badge key={attr} variant="secondary" className="text-xs">
              {attr}
            </Badge>
          ))}
        </div>
      </td>
    </>
  );
}

/** Memoized {@link TableRowCellsView}. */
const TableRowCells = memo(TableRowCellsView);
